// ─────────────────────────────────────────────────────────────────
// Glass-i — Proforma Bill To / Ship To party validation.
//
// Required: Name, Address 1, City, Pincode, Mobile, GSTIN (both parties).
//   • Pincode  — exactly 6 digits.
//   • Mobile   — one or more comma-separated Indian mobile numbers, each a
//                10-digit number starting 6/7/8/9.
//   • GSTIN    — accepts URP (unregistered), PAN, Aadhaar (12 digits) or a
//                15-char GSTIN.
// Pure helpers (no React) so they're unit-testable and reusable by the editor's
// save guard. Input FILTERS keep typing clean; VALIDATORS block save.
// ─────────────────────────────────────────────────────────────────

const PIN_RE     = /^[0-9]{6}$/;
const MOBILE_RE  = /^[6-9][0-9]{9}$/;
const PAN_RE     = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const AADHAAR_RE = /^[0-9]{12}$/;
const GSTIN_RE   = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;

const s = (v) => String(v == null ? '' : v).trim();

// ── input filters (onChange) ────────────────────────────────────
// Pincode: digits only, max 6.
export function filterPincodeInput(v) {
  return String(v == null ? '' : v).replace(/\D/g, '').slice(0, 6);
}
// Mobile: digits, commas and spaces only (multiple numbers allowed).
export function filterMobileInput(v) {
  return String(v == null ? '' : v).replace(/[^0-9,\s]/g, '');
}

// ── format validators ───────────────────────────────────────────
export function isValidPincode(v) {
  return PIN_RE.test(s(v));
}

// One or more comma-separated 10-digit Indian mobiles (start 6/7/8/9).
export function isValidIndianMobileList(v) {
  const parts = s(v).split(',').map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length === 0) return false;
  return parts.every((p) => MOBILE_RE.test(p));
}

// URP / PAN / Aadhaar / GSTIN. Case-insensitive (GSTIN/PAN are upper-cased).
export function isValidGstinLike(v) {
  const u = s(v).toUpperCase();
  if (!u) return false;
  if (u === 'URP') return true;
  return PAN_RE.test(u) || AADHAAR_RE.test(u) || GSTIN_RE.test(u);
}

// ── per-field "format error while typing" (content present + invalid) ──
export const pincodeFormatError = (v) => s(v).length > 0 && !isValidPincode(v);
export const mobileFormatError  = (v) => s(v).length > 0 && !isValidIndianMobileList(v);
export const gstinFormatError   = (v) => s(v).length > 0 && !isValidGstinLike(v);

// ── party + section validators (block save) ─────────────────────
export function validateParty(party, prefix) {
  const p = party || {};
  const errors = [];
  if (!s(p.name))          errors.push(`${prefix}: Name is required.`);
  if (!s(p.addressLine1))  errors.push(`${prefix}: Address 1 is required.`);
  if (!s(p.city))          errors.push(`${prefix}: City is required.`);

  const pin = s(p.pincode);
  if (!pin) errors.push(`${prefix}: Pincode is required.`);
  else if (!isValidPincode(pin)) errors.push(`${prefix}: Pincode must be exactly 6 digits.`);

  const mob = s(p.mobile);
  if (!mob) errors.push(`${prefix}: Mobile No is required.`);
  else if (!isValidIndianMobileList(mob)) {
    errors.push(`${prefix}: Mobile must be a 10-digit number starting 6/7/8/9 (comma-separated for multiple).`);
  }

  const gst = s(p.gstin);
  if (!gst) errors.push(`${prefix}: GSTIN is required (enter URP if unregistered).`);
  else if (!isValidGstinLike(gst)) errors.push(`${prefix}: GSTIN must be a valid GSTIN / PAN / Aadhaar, or URP.`);

  return errors;
}

// Validate both parties; returns { ok, errors[] }.
export function validateCustomerSection(billTo, shipTo) {
  const errors = [
    ...validateParty(billTo, 'Bill To'),
    ...validateParty(shipTo, 'Ship To')
  ];
  return { ok: errors.length === 0, errors };
}
